import { FormattedMessage, useIntl } from "@/i18n/i18n-client";
import Button from "@/templates/modern/components/Button/Button";
import OtpInput from "react-otp-input";
import { WhatsAppIcon } from "@/assets/Icons/WhatsAppIcon";
import { SmsIcon } from "@/assets/Icons/SmsIcon";
import { OutlineInfoCircleIcon } from "@/assets/Icons/OutlineInfoCircleIcon";
import { FeaturedOutlineIcon } from "@/assets/Icons/FeaturedOutlineIcon";
import { DefaultPhoneMethod, VerificationMethod } from "@/generated/graphql";
import { LoginMethod } from "@/templates/types/LoginFormProps";
import { useCountdown } from "@/lib/Authentication/hooks/useCountdown";
import { useEffect, useState } from "react";

interface OtpVerificationProps {
  otp: string;
  setOtp: (otp: string) => void;
  primaryButtonColor: any;
  method: LoginMethod;
  phoneMethod: DefaultPhoneMethod | undefined | null;
  numOfResend: number;
  setNumOfResend: (count: number) => void;
  isLoading: boolean;
  otpError: string | null;
  onResendOTP: () => void;
  onSwitchMethod: () => void;
  getAlternativeMethod: () => VerificationMethod | null;
}

const OtpVerification = ({
  otp,
  setOtp,
  primaryButtonColor,
  method,
  phoneMethod,
  numOfResend,
  setNumOfResend,
  isLoading,
  otpError,
  onResendOTP,
  onSwitchMethod,
  getAlternativeMethod,
}: OtpVerificationProps) => {
  const [focusedIndex, setFocusedIndex] = useState<number | null>(null);
  const [hasStartedInitialCountdown, setHasStartedInitialCountdown] =
    useState(false);
  const intl = useIntl();
  const { timeLeft, isActive, start, reset } = useCountdown({
    initialTime: 60, // 1 minute
  });
  // Start countdown only after loading is finished (for initial OTP)
  useEffect(() => {
    if (!isLoading && !hasStartedInitialCountdown) {
      start();
      setHasStartedInitialCountdown(true);
    }
  }, [start, isLoading, hasStartedInitialCountdown]);

  // Restart countdown after resend is complete
  useEffect(() => {
    if (!isLoading && hasStartedInitialCountdown && numOfResend > 0) {
      reset();
      start();
    }
  }, [isLoading, hasStartedInitialCountdown, numOfResend, reset, start]);

  // Reset the initial countdown flag when component unmounts or resets
  useEffect(() => {
    return () => {
      setHasStartedInitialCountdown(false);
    };
  }, []);

  // Format time as MM:SS
  const formatTime = (seconds: number): string => {
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = seconds % 60;
    return `${minutes}:${remainingSeconds.toString().padStart(2, "0")}`;
  };

  const handleResendClick = () => {
    const newResendCount = numOfResend + 1;
    setNumOfResend(newResendCount);
    onResendOTP();
    // Don't restart countdown immediately - wait for loading to finish
  };
  return (
    <div className="flex flex-col justify-start items-center w-full px-6 gap-2">
      <OtpInput
        value={otp}
        onChange={setOtp}
        numInputs={4}
        inputType="number"
        shouldAutoFocus
        renderInput={(props, index) => (
          <input
            {...props}
            onFocus={(e) => {
              setFocusedIndex(index);
              props.onFocus?.(e);
            }}
            onBlur={(e) => {
              setFocusedIndex(null);
              props.onBlur?.(e);
            }}
            className={`!w-14 sm:!w-20 !h-14 sm:!h-20 !text-center !text-lg sm:!text-display-lg !font-medium !rounded-lg !border text-gray-900  !transition-all  outline-none disabled:bg-gray-100`}
            style={{
              borderColor:
                focusedIndex === index
                  ? primaryButtonColor.default.backgroundColor
                  : otp?.[index] && otp[index] !== ""
                    ? primaryButtonColor.default.backgroundColor
                    : undefined,
              boxShadow:
                focusedIndex === index
                  ? `0 0 0 1px ${primaryButtonColor.default.backgroundColor}`
                  : undefined,
              color: primaryButtonColor.default.backgroundColor,
            }}
          />
        )}
        containerStyle={{
          display: "flex",
          gap: "8px",
          justifyContent: "center",
          width: "100%",
          direction: "ltr",
        }}
        placeholder="0"
      />

      <div className="flex flex-row w-full justify-start items-start gap-1 !w-full !ps-[12px] md:!ps-[44px]">
        <span
          className={`!text-gray-600 d-flex !text-sm !font-normal ${
            numOfResend == 2 && "!opacity-50"
          }`}
        >
          <FormattedMessage defaultMessage="Didn't get a code?" id="1zVLJx" />
        </span>
        {isActive ? (
          <span className="!text-gray-600 d-flex !text-sm !font-normal ">
            <FormattedMessage
              defaultMessage="Resend in {time}"
              id="xR/G45"
              values={{ time: formatTime(timeLeft) }}
            />
          </span>
        ) : (
          <Button
            className="!bg-transparent !text-gray-600 !text-sm !font-normal !underline !m-0 !p-0 !border-0 !shadow-none"
            onClick={handleResendClick}
            isDisabled={numOfResend == 2 || isLoading}
            htmlType="button"
          >
            <FormattedMessage defaultMessage="Click to resend" id="XM4l7n" />
          </Button>
        )}
      </div>

      {otpError && (
        <>
          {otpError.includes("ban") ? (
            <div className="flex flex-col  w-full justify-start items-start gap-2 !bg-error-25 p-2 !rounded-md border border-error-300">
              <FeaturedOutlineIcon className="!text-error-600" />
              <span className="text-error-700 text-sm !font-semibold">
                <FormattedMessage defaultMessage="Account banned" id="VvhlYv" />
              </span>
              <span className="text-error-700 text-sm">
                <FormattedMessage
                  defaultMessage="You’re banned from shopping from this store. Please contact the store owner to be unbanned"
                  id="2zHTx6"
                />
              </span>

              <span className="text-error-700 text-sm !font-semibold">
                <FormattedMessage defaultMessage="Contact us" id="WnQ/AZ" />
              </span>
            </div>
          ) : (
            <div className="flex  w-full justify-start items-center gap-2 !bg-error-25 p-2 !rounded-md border border-error-300">
              <FeaturedOutlineIcon className="!text-error-600" />
              <span className="text-error-700 text-sm">{otpError}</span>
            </div>
          )}
        </>
      )}

      {numOfResend == 2 && !isLoading && (
        <div className="flex flex-row w-full justify-start items-start gap-4 border border-gray-200 pl-4 p-8 !rounded-xl !bg-gray-25">
          <OutlineInfoCircleIcon />
          <div className="flex flex-col justify-start items-start gap-1 w-full">
            <span className="!text-gray-600 !text-sm !font-semibold ">
              <FormattedMessage
                defaultMessage="Still didn't get the code?"
                id="sBfmnz"
              />
            </span>
            {getAlternativeMethod() ? (
              <>
                <span className="text-gray-600 !text-sm !font-normal ">
                  <FormattedMessage
                    defaultMessage="Try verifying with"
                    id="5ZdsaA"
                  />
                </span>
                <Button
                  className="!rounded-lg !font-semibold flex-1"
                  onClick={onSwitchMethod}
                  isDisabled={isLoading}
                  htmlType="button"
                  type="secondary"
                  fullWidth
                >
                  {phoneMethod === DefaultPhoneMethod.Sms ? (
                    <WhatsAppIcon />
                  ) : (
                    <SmsIcon />
                  )}
                  {phoneMethod === DefaultPhoneMethod.Sms ? (
                    <FormattedMessage defaultMessage="Whatsapp" id="8jnPFi" />
                  ) : (
                    <FormattedMessage defaultMessage="SMS" id="k5NtsF" />
                  )}
                </Button>
              </>
            ) : (
              <span className="text-gray-600 !text-sm !font-normal">
                <FormattedMessage
                  defaultMessage="You've reached the max limit for sending OTPs. Please try again later and make sure to use a valid {method}."
                  id="5dGqCg"
                  values={{
                    method:
                      method === LoginMethod.EMAIL
                        ? intl.formatMessage({
                            defaultMessage: "email",
                            id: "yUVl6+",
                          })
                        : phoneMethod === DefaultPhoneMethod.Sms
                          ? intl.formatMessage({
                              defaultMessage: "SMS",
                              id: "k5NtsF",
                            })
                          : intl.formatMessage({
                              defaultMessage: "WhatsApp",
                              id: "5ow6dg",
                            }),
                  }}
                />
              </span>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default OtpVerification;
